import type { z } from 'zod';

const KEY_PREFIX = 'seans.';

/**
 * A typed, schema-validated value in localStorage. Corrupt or outdated entries fall back to the
 * default instead of crashing the app; storage failures (private mode, quota) are non-fatal.
 */
export interface StoredValue<T> {
  read(): T;
  write(value: T): void;
  remove(): void;
}

export const storageKey = (key: string): string => `${KEY_PREFIX}${key}`;

export const createStoredValue = <S extends z.ZodType>(
  key: string,
  schema: S,
  fallback: z.output<S>,
  area: 'local' | 'session' = 'local',
): StoredValue<z.output<S>> => {
  const fullKey = storageKey(key);
  const storage = (): Storage | null => {
    try {
      return area === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
      return null;
    }
  };
  return {
    read() {
      const raw = storage()?.getItem(fullKey);
      if (raw === null || raw === undefined) return fallback;
      try {
        const parsed = schema.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data : fallback;
      } catch {
        return fallback;
      }
    },
    write(value) {
      try {
        storage()?.setItem(fullKey, JSON.stringify(value));
      } catch {
        // Quota or privacy mode: the preference simply is not remembered.
      }
    },
    remove() {
      try {
        storage()?.removeItem(fullKey);
      } catch {
        // See write().
      }
    },
  };
};
